import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Mail, Lock, Volume2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { initAudio, playPointsSound } from '../components/Animations';
import AvatarCreation from '../components/AvatarCreation';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAvatarCreation, setShowAvatarCreation] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const { login, updateUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Test sound function
  const handleTestSound = () => {
    initAudio();
    playPointsSound();
    setSoundEnabled(true);
    toast({
      title: "🔊 Sound Test",
      description: "You should hear a notification sound!",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Initialize audio on user interaction (login click)
    initAudio();

    try {
      const user = await login(email, password);
      setLoggedInUser(user);
      
      // Check if user needs to create avatar
      if (!user.has_avatar && !user.avatar_url) {
        toast({
          title: "Welcome!",
          description: "Let's create your avatar first!",
        });
        setShowAvatarCreation(true);
        return;
      }
      
      toast({
        title: "Welcome back!",
        description: `Logged in as ${user.name}`,
      });

      // Route based on user role
      if (user.role === 'heavenly_service_centre') {
        navigate('/service-centre');
      } else if (user.role === 'participant') {
        navigate('/participant');
      } else {
        navigate('/admin');
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
      toast({
        title: "Login failed",
        description: err.response?.data?.detail || "Invalid credentials",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarCreated = (avatarUrl) => {
    // Update the user in context with new avatar
    updateUser({ avatar_url: avatarUrl, has_avatar: true });
    
    toast({
      title: "🎉 Avatar Created!",
      description: "Your profile is now complete!",
    });
    
    // Route based on user role
    if (loggedInUser?.role === 'heavenly_service_centre') {
      navigate('/service-centre');
    } else if (loggedInUser?.role === 'participant') {
      navigate('/participant');
    } else {
      navigate('/admin');
    }
  };

  // Handle skipping avatar creation
  const handleSkipAvatar = () => {
    setShowAvatarCreation(false);
    
    toast({
      title: "Avatar skipped",
      description: "You can create one later from your profile!",
    });
    
    // Route based on user role
    if (loggedInUser?.role === 'heavenly_service_centre') {
      navigate('/service-centre');
    } else if (loggedInUser?.role === 'participant') {
      navigate('/participant');
    } else {
      navigate('/admin');
    }
  };

  // Show avatar creation screen
  if (showAvatarCreation) {
    return (
      <AvatarCreation 
        onAvatarCreated={handleAvatarCreated}
        isRequired={true}
      />
    );
  }

  return (
    <div 
      className="min-h-screen flex relative"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1771002382315-9be24abde4e4?w=1920&q=80')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Single overlay for entire screen */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/60"></div>
      
      {/* Left Side - Login Form (SWAPPED) */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 relative">
        {/* Glass card for form */}
        <div className="w-full max-w-md relative z-10 bg-black/50 rounded-3xl p-6 md:p-8 border border-white/30 shadow-2xl">
          <div className="text-center mb-8">
            {/* Mobile logo - only shows on smaller screens */}
            <div className="lg:hidden mb-6">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/8545441a-c3d9-41e6-8278-ba9e07194dc1/images/70037796702ae9e4efc4e7495ad96c7e813b1bfc7c1481b6f0890daca2e995d6.png" 
                alt="EMS Solution - My Guest Program Logo" 
                className="w-32 h-32 object-contain mx-auto drop-shadow-[0_0_20px_rgba(0,212,255,0.4)]"
              />
              <p className="text-white/80 text-xs mt-2">Fully Integrated Guest Experience Management System</p>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">Welcome Back</h2>
            <p className="text-white/80">Sign in to continue to your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-white/80">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-cyan-400 w-5 h-5" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="your.email@hotel.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white placeholder-white/40 rounded-xl focus:border-cyan-400 focus:ring-cyan-400/20"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-white/80">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-cyan-400 w-5 h-5" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white placeholder-white/40 rounded-xl focus:border-cyan-400 focus:ring-cyan-400/20"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center bg-red-500/10 py-2 rounded-lg border border-red-500/20">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              className="w-full h-14 bg-gradient-to-r from-[#ffd700] to-[#f0c030] hover:from-[#ffe44d] hover:to-[#ffd700] text-black font-bold rounded-xl shadow-lg shadow-[#ffd700]/20 hover:shadow-[#ffd700]/40 transition-all text-lg"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="mr-2 h-5 w-5" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="text-white/70 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#ffd700] hover:text-[#ffe44d] font-semibold">
                Register here
              </Link>
            </p>
            <div className="border-t border-white/10 pt-3">
              <Link 
                to="/pin-login" 
                className="inline-flex items-center gap-2 text-white/60 hover:text-purple-400 text-sm transition-colors"
              >
                <span className="text-lg">📱</span>
                HSC Tablet Quick Login (PIN)
              </Link>
            </div>
            {/* QR Code - Download App */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex flex-col items-center gap-2">
                <div className="bg-white rounded-xl p-2 shadow-lg">
                  <img 
                    src="/assets/login_qr.png" 
                    alt="Scan to download app" 
                    className="w-24 h-24 object-contain"
                    data-testid="login-qr-code-mobile"
                  />
                </div>
                <p className="text-white/60 text-xs">Scan to install app on your device</p>
              </div>
            </div>

            {/* Clear Cache & Reload */}
            <div className="border-t border-white/10 pt-3">
              <button
                data-testid="landing-clear-cache-btn"
                onClick={async () => {
                  try {
                    if ('caches' in window) {
                      const names = await caches.keys();
                      await Promise.all(names.map(name => caches.delete(name)));
                    }
                    if ('serviceWorker' in navigator) {
                      const registrations = await navigator.serviceWorker.getRegistrations();
                      await Promise.all(registrations.map(r => r.unregister()));
                    }
                  } catch (e) { /* ignore */ }
                  window.location.reload(true);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 hover:border-cyan-400/40 text-white/80 hover:text-white text-sm font-medium transition-all"
              >
                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg>
                Clear cache memory &amp; reload newest app
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Branding (SWAPPED) */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white w-full">
          {/* EMS Solution Logo V4 - With rounded corners */}
          <div className="mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_guest-joy-track/artifacts/02z0wfko_70037796702ae9e4efc4e7495ad96c7e813b1bfc7c1481b6f0890daca2e995d6.png" 
              alt="EMS Solution - My Guest Program Logo" 
              className="w-72 h-72 object-contain rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5),0_0_60px_rgba(212,175,55,0.2)]"
            />
          </div>
          
          {/* Main Title with TM marks */}
          <h2 className="text-3xl font-bold text-white mb-3 text-center drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            EMS<span className="text-[#ffd700] text-lg align-super ml-0.5">™</span>
            <span className="ml-2">Solution</span>
            <span className="mx-2 text-white/60">-</span>
            My Guest Program<span className="text-[#ffd700] text-lg align-super ml-0.5">™</span>
          </h2>
          
          {/* Tagline */}
          <div className="bg-black/40 px-6 py-3 rounded-xl mb-6">
            <p className="text-white text-lg tracking-wide text-center font-medium">
              Fully Integrated Guest Experience Management System
            </p>
          </div>
          
          {/* Dreams Rose Hall Picture */}
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_guest-joy-track/artifacts/6yucncda_20250702_2048427E2.jpg" 
              alt="Dreams Rose Hall Jamaica" 
              className="max-w-xs w-full h-auto rounded-2xl shadow-2xl border border-white/30"
            />
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-1.5 rounded-full border border-white/30">
              <span className="text-white text-sm font-semibold">Dreams Rose Hall, Jamaica</span>
            </div>
          </div>

          {/* QR Code - Download App */}
          <div className="mt-8 flex flex-col items-center">
            <div className="bg-white rounded-2xl p-3 shadow-xl border border-white/40">
              <img 
                src="/assets/login_qr.png" 
                alt="Scan to download app" 
                className="w-28 h-28 object-contain"
                data-testid="landing-qr-code"
              />
            </div>
            <p className="text-white/90 text-sm font-medium mt-3 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              Scan to install app on your device
            </p>
          </div>
        </div>
      </div>
      
      {/* Parent Company Logo - Bottom Left */}
      <div className="absolute bottom-4 left-4 z-20 flex items-center gap-2 bg-black/40 px-3 py-2 rounded-xl border border-white/20">
        <img 
          src="https://customer-assets.emergentagent.com/job_guest-joy-track/artifacts/ukua4had_fdf53510107c1fbe140ddbdf0679a9675d936af70fa8c13fd881505557f8269b.png" 
          alt="Heavenly AI Solutions" 
          className="w-10 h-10 object-contain"
        />
        <div className="text-white">
          <p className="text-xs font-semibold leading-tight">Heavenly AI</p>
          <p className="text-[10px] text-white/70 leading-tight">Solutions™</p>
        </div>
      </div>

      {/* Brand Footer */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
        <p className="text-white/40 text-[10px] tracking-wide">
          Developed by <span className="text-cyan-400/60 font-medium">Heavenly AI Solutions</span> led by <span className="text-white/50 font-medium">Andres Mocny</span>
        </p>
      </div>
    </div>
  );
};

export default Login;